/**
 * FAQ Manager - Moon Moon Agency
 *
 * - Highlight l'ancre active via IntersectionObserver (categorie la plus haute
 *   actuellement visible dans la zone "lecture" du viewport).
 * - Click sur ancre : smooth scroll vers la categorie (scroll-margin-top
 *   gere l'offset header sticky).
 * - Sur mobile, le bandeau nav scroll-x suit l'ancre active (auto-scroll
 *   horizontal pour centrer le link actif).
 * - Accordeon <details> : un seul item ouvert a la fois par categorie
 *   (close-others scoped).
 */

const FAQManager = {
	config: {
		selectors: {
			root: '.mm-faq',
			nav: '[data-faq-nav]',
			navLink: '[data-faq-nav-link]',
			content: '[data-faq-content]',
			category: '[data-faq-category]',
			item: '[data-faq-item]',
		},
		classes: {
			active: 'mm-active',
		},
		observerOptions: {
			/* Detection zone : tiers haut du viewport.
			   Top -20% : on attend que la categorie passe sous l'header.
			   Bottom -60% : on prend la 1ere categorie qui touche le haut. */
			rootMargin: '-20% 0px -60% 0px',
			threshold: 0,
		},
	},

	state: {
		observer: null,
		activeId: null,
	},

	init() {
		const root = document.querySelector(this.config.selectors.root);
		if (!root) return;

		this._bindEvents(root);
		this._initObserver(root);
		window.log('info', '❓ [FAQ]', 'Initialized');
	},

	_bindEvents(root) {
		root.addEventListener('click', (e) => {
			/* Click sur ancre nav : smooth scroll + active immediat */
			const link = e.target.closest(this.config.selectors.navLink);
			if (link) {
				const targetId = link.dataset.target;
				const target = targetId && document.getElementById(targetId);
				if (target) {
					e.preventDefault();
					target.scrollIntoView({ behavior: 'smooth', block: 'start' });
					this._setActive(targetId);
				}
				return;
			}

			/* Click sur summary : close-others scoped a la categorie parente */
			const summary = e.target.closest('summary');
			if (summary && summary.parentElement?.matches(this.config.selectors.item)) {
				const detail = summary.parentElement;
				/* setTimeout pour laisser le toggle natif s'appliquer d'abord
				   (sinon detail.open est encore l'etat precedent). */
				setTimeout(() => {
					if (!detail.open) return;
					const category = detail.closest(this.config.selectors.category);
					if (!category) return;
					category.querySelectorAll(this.config.selectors.item).forEach(d => {
						if (d !== detail && d.open) d.open = false;
					});
				}, 0);
			}
		});
	},

	_initObserver(root) {
		if (!('IntersectionObserver' in window)) return;

		const categories = root.querySelectorAll(this.config.selectors.category);
		if (!categories.length) return;

		this.state.observer = new IntersectionObserver((entries) => {
			const visible = entries
				.filter(entry => entry.isIntersecting)
				.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
			if (!visible.length) return;
			this._setActive(visible[0].target.id);
		}, this.config.observerOptions);

		categories.forEach(cat => this.state.observer.observe(cat));
	},

	_setActive(id) {
		if (!id || this.state.activeId === id) return;
		this.state.activeId = id;

		const root = document.querySelector(this.config.selectors.root);
		if (!root) return;

		root.querySelectorAll(this.config.selectors.navLink).forEach(link => {
			const isActive = link.dataset.target === id;
			link.classList.toggle(this.config.classes.active, isActive);
			if (isActive) this._scrollNavToLink(link);
		});
	},

	/**
	 * Sur mobile, centre le link actif dans le bandeau scrollable horizontal.
	 * No-op sur desktop (nav verticale, pas de scroll horizontal).
	 */
	_scrollNavToLink(link) {
		const nav = link.closest(this.config.selectors.nav);
		if (!nav) return;
		if (nav.scrollWidth <= nav.clientWidth) return;

		const linkRect = link.getBoundingClientRect();
		const navRect = nav.getBoundingClientRect();
		const targetScroll = nav.scrollLeft + linkRect.left - navRect.left - (navRect.width / 2) + (linkRect.width / 2);
		nav.scrollTo({ left: targetScroll, behavior: 'smooth' });
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => FAQManager.init());
} else {
	FAQManager.init();
}

window.FAQManager = FAQManager;
